import { Rule } from 'postcss'
import {
	ListMap,
	getDeclarationNodes,
	isMediaRule,
	buildRootRulesDeclarations,
	isVariableDeclarationRule,
} from '../helpers'

const plugin = () => {
	const rulesetMap = new ListMap<Rule>()

	const removeDuplicates = () => {
		rulesetMap.iterateDuplicates((rules) => {
			/**
			 * After traversing the tree, we have 4 potential cases per selector:
			 * 1. All media queries have the same declaration duplication.
			 * 2. All media queries are bound (min and max), so we can attempt to clean specific queries,
			 * because there's no fear that another query will "leak" and somehow change the UI.
			 * 3. Some media queries are supersets of others (for example, 320-750 and 320-1000) and have duplications so we can clean the subset's declarations.
			 * 4. All media queries have the same declaration duplication, but the root doesn't, so they can be unified.
			 *
			 * Our POC concluded that cases 2-4 provide very little value (0.01% size decrease),
			 * so we don't bother with them.
			 */

			const potentialDeclarationsForDeletion = buildRootRulesDeclarations(rules)

			// If we don't have any declarations on the root, there's not much we can do
			if (potentialDeclarationsForDeletion.size === 0) {
				return
			}

			const mediaRules = rules.filter(isMediaRule)

			/**
			 * First we go over all the media rules to root out any declarations
			 * that are NOT duplicated across the board (i.e. different in at least one media rule).
			 */
			mediaRules.forEach((rule) => {
				getDeclarationNodes(rule).forEach((d) => {
					const isNonDuplicatedDeclaration = d.value !== potentialDeclarationsForDeletion.get(d.prop)

					if (isNonDuplicatedDeclaration) {
						potentialDeclarationsForDeletion.delete(d.prop)
					}
				})
			})

			// We're left with no duplicated rules, nothing to do here
			if (potentialDeclarationsForDeletion.size === 0) {
				return
			}

			// Go over all media rules once more and attempt to clean all duplicate declarations.
			mediaRules.forEach((rule) => {
				getDeclarationNodes(rule).forEach((d) => {
					const rootHasDeclaration = potentialDeclarationsForDeletion.has(d.prop)

					if (rootHasDeclaration) {
						d.remove()
					}
				})
			})
		})
	}

	return {
		postcssPlugin: 'redundantMediaQueriesSquasher',
		Rule: {
			'*': (r: Rule) => {
				if (isVariableDeclarationRule(r)) {
					return
				}

				rulesetMap.add(r.selector, r)
			},
		},
		OnceExit() {
			removeDuplicates()
		},
	}
}

plugin.postcss = true as true

export default plugin
